/**
 * The one data-colour rule (TMMAPP-0037). An entity's categorical colour is chosen by its id, so
 * an envelope, a category or an account keeps its colour everywhere and when a list changes. The
 * INDEX is the same in both modes; the colours come from the active mode's brand palette. No
 * other place computes a data colour — and a colour never carries meaning alone (every coloured
 * element keeps its text label).
 */
import type { CategoricalColor } from './brandTheme';
import { MOOD_FACE_COLOURS, type AppTokens, type ThemeMode } from './tokens';

export function categoricalIndex(id: number, size = 8): number {
  return ((Math.trunc(id) % size) + size) % size;
}

export function categoricalFor(
  id: number,
  tokens: Pick<AppTokens, 'categorical'>,
): CategoricalColor {
  const palette = tokens.categorical;
  return palette[categoricalIndex(id, palette.length)] as CategoricalColor;
}

/**
 * The v2 prototype's five named tints (TMMAPP-0150, brand-v2.md "Categorical"): suggestion and
 * tool tiles, kind chips and avatars take a tint by NAME, and the name is a fixed index into the
 * active brand's categorical palette (sage 1, peach 2, lavender 3, rose 4, sky 5; zero-based here).
 * A tile paints the tint's `soft` with its `fill` icon; text on a `soft` is `text` (guaranteed).
 */
export const TINTS = { sage: 0, peach: 1, lavender: 2, rose: 3, sky: 4 } as const;
export type TintName = keyof typeof TINTS;

export function tintFor(name: TintName, tokens: Pick<AppTokens, 'categorical'>): CategoricalColor {
  return tokens.categorical[TINTS[name]] as CategoricalColor;
}

/**
 * The Home mood faces (TMMAPP-0150 for TMMAPP-0152; TMMAPP-0154 §A 5), lowest mood first. Each face
 * paints the prototype's saturated fill and dark features from `MOOD_FACE_COLOURS` (`tokens.ts`),
 * with designed dark-mode equivalents — illustration, not text, and never the categorical `soft`s.
 * The mood's name always sits beside it, so the colour never carries the meaning alone.
 */
export const MOOD_FACES = [
  { mood: 'stressed' },
  { mood: 'overwhelmed' },
  { mood: 'okay' },
  { mood: 'good' },
  { mood: 'confident' },
] as const;
export type Mood = (typeof MOOD_FACES)[number]['mood'];

/** A mood face's fill and feature ink in `mode`. */
export function moodFaceColours(mood: Mood, mode: ThemeMode): { fill: string; features: string } {
  const set = MOOD_FACE_COLOURS[mode];
  return { fill: set[mood], features: set.features };
}
